import Component from '@glimmer/component'
import { t } from 'ember-intl'
import type { TOC } from '@ember/component/template-only'
import { parseISO } from 'date-fns'
import Disappointed from '@images/emoji-disappointed.svg'
import Smile from '@images/emoji-smile.svg'
import Unsure from '@images/emoji-unsure.svg'

const defaultDateFormat = new Intl.DateTimeFormat('en-AU', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const ReflectionImages = {
  high: Smile,
  medium: Unsure,
  low: Disappointed,
}

const isReflectionKey = (ref?: string): ref is keyof typeof ReflectionImages => Boolean(ref && ref in ReflectionImages)

interface RecentWritingStatsItemSignature {
  Args: {
    name?: string
  }
  Blocks: {
    default: []
  }
}

interface RecentWritingCardData {
  studentId: string
  firstName: string
  lastName: string
  avatar?: string
  className?: string
  activityId: string
  activityTitle?: string
  createdDate: string
  studentAnswers: string[]
  map?: number
  lesson?: number
  placedAt?: string
  reflection?: string
}

interface RecentWritingCardSignature {
  Element: HTMLLIElement
  Args: RecentWritingCardData
}

const getAvatarFullPath = (avatar = 'avatar-001') => {
  return `https://content-assets.static.readingeggs.com/writing-legends/activities/common/images/avatars/${avatar}.png`
}

const RecentWritingStatsItem: TOC<RecentWritingStatsItemSignature> = <template>
  <div class="flex flex-col lg:flex-row"><div
      class="text-dusty-black-200 text-center uppercase lg:text-left"
    >{{@name}}</div><div class="ml-1 text-center lg:text-left">{{yield}}</div></div>
</template>

export class RecentWritingCardLesson extends Component<RecentWritingCardSignature> {
  get avatarFullPath() {
    return getAvatarFullPath(this.args.avatar)
  }

  get createdDate() {
    return defaultDateFormat.format(parseISO(this.args.createdDate))
  }

  get reflectionEmoji() {
    if (!isReflectionKey(this.args.reflection)) return null
    return ReflectionImages[this.args.reflection]
  }

  <template>
    <li class="flex min-h-24 border-2 p-2 shadow-lg" ...attributes>
      <div class="basis-80 text-center">
        <img
          width="60"
          class="mx-auto"
          alt={{t "pages.recentWriting.card.studentAvatarAlt"}}
          src={{this.avatarFullPath}}
        />
        <div class="font-bold">{{@firstName}} {{@lastName}}</div>
        <div>{{@className}}</div>
      </div>
      <div class="basis-full">
        <div class="flex justify-between border-b p-1">
          <RecentWritingStatsItem @name="When">{{this.createdDate}}</RecentWritingStatsItem>
          {{#if @map}}
            <RecentWritingStatsItem @name="Map">{{@map}}</RecentWritingStatsItem>
          {{/if}}

          {{#if @lesson}}
            <RecentWritingStatsItem @name="Lesson">{{@lesson}}</RecentWritingStatsItem>
          {{/if}}

          {{#if this.reflectionEmoji}}
            <RecentWritingStatsItem @name="Student Reflection">
              <img src={{this.reflectionEmoji}} alt={{t "pages.recentWriting.card.studentReflectionAlt"}} class="w-8" />
            </RecentWritingStatsItem>
          {{/if}}

          {{#if @placedAt}}
            <RecentWritingStatsItem @name="Placed At">{{@placedAt}}</RecentWritingStatsItem>
          {{/if}}
        </div>
        <div class="flex items-center">
          <div class="w-1/6 flex-none font-bold">{{@activityTitle}}</div>
          <div class="mx-8 my-4 grow space-y-3">
            {{#each @studentAnswers as |answer|}}
              <p>{{answer}}</p>
            {{/each}}
          </div>
        </div>
      </div>
    </li>
  </template>
}

export default RecentWritingCardLesson
